import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import {
  Check,
  Eye,
  EyeOff,
  ImageOff,
  Images,
  ImageUp,
  Link2,
  Loader2,
  Pencil,
  Plus,
  Search,
  Trash2,
  Upload,
  X,
} from 'lucide-react';
import api from '../services/api';
import { assetUrl } from '../utils/assets';
import ActionMenu from '../components/common/ActionMenu';

/** Matches the API's multer limit for gallery uploads. */
const MAX_FILE_BYTES = 10 * 1024 * 1024;

/** Every write endpoint is multipart, and only multipart. */
const MULTIPART_CONFIG = { headers: { 'Content-Type': 'multipart/form-data' } };

/** The admin shell already renders the page title, so this page starts with its toolbar. */
const EMPTY_FORM = {
  title: '',
  category: '',
  displayOrder: '0',
  source: 'upload', // 'upload' | 'url'
  imageUrl: '',
  imageFile: null,
  isActive: true,
};

/**
 * Resolve a stored gallery path for the browser.
 *
 * `assetUrl` maps anything the API serves ("/uploads/gallery/x.jpg") onto the
 * API origin and leaves absolute URLs untouched. The seeded rows point at
 * images bundled with this app ("/images/1.jpg"), which the API does not serve,
 * so those stay origin-relative — the same rule HomePage.jsx applies.
 */
const resolveImageSrc = (path) => {
  const stored = (path || '').trim();
  if (!stored) return null;
  if (/^\/(images|assets)\//i.test(stored)) return stored;
  return assetUrl(stored);
};

const orderOf = (image) => Number(image?.display_order) || 0;
const byDisplayOrder = (a, b) => orderOf(a) - orderOf(b) || (Number(a.id) || 0) - (Number(b.id) || 0);
const titleOf = (image) => (image?.title || '').trim() || 'this image';

/** 36px square icon-only control: the design system's minimum touch target. */
const IconButton = ({ label, hint, onClick, disabled = false, tone = 'default', children }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    aria-label={label}
    title={hint || label}
    className={[
      'inline-flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg border transition-colors',
      'disabled:cursor-not-allowed disabled:opacity-50',
      tone === 'danger'
        ? 'border-ink-200 bg-white text-ink-500 hover:border-gold-300 hover:bg-gold-50 hover:text-gold-800'
        : 'border-ink-200 bg-white text-ink-600 hover:border-brand-200 hover:bg-brand-50 hover:text-brand-700',
    ].join(' ')}
  >
    {children}
  </button>
);

const SkeletonGrid = () => (
  <div
    className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
    aria-busy="true"
  >
    <p className="sr-only">Loading gallery images…</p>
    {Array.from({ length: 8 }).map((_, index) => (
      <div
        key={index}
        aria-hidden="true"
        className="overflow-hidden rounded-xl border border-ink-200 bg-white shadow-card"
      >
        <div className="aspect-[4/3] w-full animate-pulse bg-ink-100" />
        <div className="space-y-2 p-4">
          <div className="h-3 w-3/4 animate-pulse rounded bg-ink-100" />
          <div className="h-5 w-20 animate-pulse rounded-full bg-ink-100" />
          <div className="h-9 w-full animate-pulse rounded-lg bg-ink-100" />
        </div>
      </div>
    ))}
  </div>
);

const AdminGallery = () => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [filePreview, setFilePreview] = useState('');
  const [failedPreview, setFailedPreview] = useState(null);
  const [brokenIds, setBrokenIds] = useState({});

  const savingRef = useRef(false);
  const titleInputRef = useRef(null);
  const lastFocusedRef = useRef(null);

  /* ── Data ─────────────────────────────────────────────────────────────── */

  const loadImages = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/gallery/admin/all');
      setImages(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Failed to load the gallery:', error);
      toast.error(error.response?.data?.message || 'Could not load the gallery images');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadImages();
  }, [loadImages]);

  const filteredImages = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) return images;
    return images.filter((image) =>
      `${image.title || ''} ${image.category || ''}`.toLowerCase().includes(query)
    );
  }, [images, searchTerm]);

  const categories = useMemo(
    () =>
      Array.from(
        new Set(images.map((image) => (image.category || '').trim()).filter(Boolean))
      ).sort((a, b) => a.localeCompare(b)),
    [images]
  );

  const nextOrder = useMemo(
    () => images.reduce((highest, image) => Math.max(highest, orderOf(image)), 0) + 1,
    [images]
  );

  const markBroken = useCallback((id) => {
    setBrokenIds((previous) => (previous[id] ? previous : { ...previous, [id]: true }));
  }, []);

  /* ── Dialog open / close ──────────────────────────────────────────────── */

  useEffect(() => {
    savingRef.current = saving;
  }, [saving]);

  const resetForm = useCallback(() => {
    setIsFormOpen(false);
    setEditing(null);
    setForm(EMPTY_FORM);
    setFailedPreview(null);
    const previous = lastFocusedRef.current;
    lastFocusedRef.current = null;
    if (previous && typeof previous.focus === 'function') previous.focus();
  }, []);

  const closeForm = useCallback(() => {
    if (savingRef.current) return;
    resetForm();
  }, [resetForm]);

  /** Body scroll lock, active only while the dialog is mounted. */
  useEffect(() => {
    if (!isFormOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isFormOpen]);

  /** Escape closes the dialog. */
  useEffect(() => {
    if (!isFormOpen) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') closeForm();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isFormOpen, closeForm]);

  /** Move focus into the dialog when it opens. */
  useEffect(() => {
    if (!isFormOpen) return undefined;
    const timer = window.setTimeout(() => titleInputRef.current?.focus(), 0);
    return () => window.clearTimeout(timer);
  }, [isFormOpen]);

  /** Object URL for the picked file, revoked when it is replaced. */
  useEffect(() => {
    if (!form.imageFile) {
      setFilePreview('');
      return undefined;
    }
    const objectUrl = URL.createObjectURL(form.imageFile);
    setFilePreview(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [form.imageFile]);

  const openCreate = useCallback(() => {
    lastFocusedRef.current = document.activeElement;
    setEditing(null);
    setFailedPreview(null);
    setForm({ ...EMPTY_FORM, displayOrder: String(nextOrder) });
    setIsFormOpen(true);
  }, [nextOrder]);

  const openEdit = useCallback((image) => {
    lastFocusedRef.current = document.activeElement;
    const storedUrl = image.image_url || image.src || '';
    setEditing(image);
    setFailedPreview(null);
    setForm({
      title: image.title || '',
      category: image.category || '',
      displayOrder: String(orderOf(image)),
      source: storedUrl.startsWith('/uploads') ? 'upload' : 'url',
      imageUrl: storedUrl,
      imageFile: null,
      isActive: image.is_active !== false,
    });
    setIsFormOpen(true);
  }, []);

  /* ── Form handlers ────────────────────────────────────────────────────── */

  const updateField = (field) => (event) => {
    const { value } = event.target;
    setForm((previous) => ({ ...previous, [field]: value }));
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0] || null;
    setFailedPreview(null);

    if (!file) {
      setForm((previous) => ({ ...previous, imageFile: null }));
      return;
    }

    if (!file.type.startsWith('image/')) {
      toast.error('Please choose an image file (JPEG, PNG, GIF or WebP)');
      setForm((previous) => ({ ...previous, imageFile: null }));
      event.target.value = '';
      return;
    }

    if (file.size > MAX_FILE_BYTES) {
      const size = (file.size / (1024 * 1024)).toFixed(1);
      toast.error(`That image is ${size} MB. The maximum size is 10 MB.`);
      setForm((previous) => ({ ...previous, imageFile: null }));
      event.target.value = '';
      return;
    }

    setForm((previous) => ({ ...previous, imageFile: file }));
  };

  const previewSrc = useMemo(() => {
    const storedCurrent = editing ? editing.image_url || editing.src : '';
    if (form.source === 'upload') {
      return filePreview || resolveImageSrc(storedCurrent) || '';
    }
    return resolveImageSrc(form.imageUrl) || resolveImageSrc(storedCurrent) || '';
  }, [form.source, form.imageUrl, filePreview, editing]);

  const previewFailed = Boolean(previewSrc) && failedPreview === previewSrc;

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (saving) return;

    const title = form.title.trim();
    const category = form.category.trim();
    const parsedOrder = Number.parseInt(form.displayOrder, 10);
    const displayOrder = Number.isFinite(parsedOrder) && parsedOrder >= 0 ? parsedOrder : 0;
    const pastedUrl = form.imageUrl.trim();
    const currentUrl = editing ? editing.image_url || editing.src || '' : '';
    const isUpload = form.source === 'upload';

    if (!title) {
      toast.error('Please enter a title for this image');
      return;
    }
    if (isUpload && !form.imageFile && !currentUrl) {
      toast.error('Choose an image file to upload');
      return;
    }
    if (!isUpload && !pastedUrl) {
      toast.error('Paste an image URL, or switch to uploading a file');
      return;
    }

    const payload = new FormData();
    payload.append('title', title);
    payload.append('category', category || 'Gallery');
    payload.append('display_order', String(displayOrder));
    if (isUpload) {
      // A file and an image_url are mutually exclusive.
      if (form.imageFile) payload.append('image', form.imageFile);
    } else if (pastedUrl) {
      payload.append('image_url', pastedUrl);
    }
    if (editing) {
      payload.append('is_active', form.isActive ? 'true' : 'false');
    }

    setSaving(true);
    try {
      if (editing) {
        const { data } = await api.put(`/gallery/${editing.id}`, payload, MULTIPART_CONFIG);
        setImages((previous) =>
          previous.map((image) => (image.id === data.id ? data : image)).sort(byDisplayOrder)
        );
        toast.success('Image updated');
      } else {
        const { data } = await api.post('/gallery', payload, MULTIPART_CONFIG);
        setImages((previous) => [...previous, data].sort(byDisplayOrder));
        toast.success('Image added to the gallery');
      }
      resetForm();
    } catch (error) {
      console.error('Failed to save the gallery image:', error);
      toast.error(error.response?.data?.message || 'Could not save the image');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (image) => {
    if (busyId) return;
    const makeVisible = image.is_active === false;

    setBusyId(image.id);
    try {
      const payload = new FormData();
      payload.append('is_active', makeVisible ? 'true' : 'false');
      const { data } = await api.put(`/gallery/${image.id}`, payload, MULTIPART_CONFIG);
      setImages((previous) =>
        previous.map((item) => (item.id === data.id ? data : item)).sort(byDisplayOrder)
      );
      toast.success(
        makeVisible ? 'Image is visible on the homepage again' : 'Image hidden from the homepage'
      );
    } catch (error) {
      console.error('Failed to change gallery visibility:', error);
      toast.error(error.response?.data?.message || 'Could not update the image');
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (image) => {
    if (busyId) return;
    const confirmed = window.confirm(
      `Delete "${titleOf(image)}"? It will disappear from the homepage and its file will be removed.`
    );
    if (!confirmed) return;

    setBusyId(image.id);
    try {
      await api.delete(`/gallery/${image.id}`);
      setImages((previous) => previous.filter((item) => item.id !== image.id));
      toast.success('Image deleted');
    } catch (error) {
      console.error('Failed to delete the gallery image:', error);
      toast.error(error.response?.data?.message || 'Could not delete the image');
    } finally {
      setBusyId(null);
    }
  };

  /* ── Render ───────────────────────────────────────────────────────────── */

  return (
    <div>
      {/* Toolbar — the page title lives in the admin header. */}
      <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
        <div className="flex flex-wrap items-center gap-3">
          <p className="text-sm text-ink-500">
            {loading ? (
              'Loading gallery…'
            ) : (
              <>
                <span className="font-semibold text-ink-900">{images.length}</span>{' '}
                {images.length === 1 ? 'image' : 'images'}
                {searchTerm.trim() && filteredImages.length !== images.length && (
                  <span className="text-ink-400"> · {filteredImages.length} shown</span>
                )}
              </>
            )}
          </p>
          <div className="relative">
            <Search
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-400"
              aria-hidden="true"
            />
            <input
              type="search"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Search title or category"
              aria-label="Search gallery images by title or category"
              className="input-field w-full py-2 pl-9 text-sm sm:w-64"
            />
          </div>
        </div>

        <button type="button" onClick={openCreate} className="btn-primary">
          <Plus className="h-4 w-4" aria-hidden="true" />
          Add image
        </button>
      </div>

      {loading && <SkeletonGrid />}

      {!loading && images.length === 0 && (
        <div className="rounded-xl border border-ink-200 bg-white p-10 text-center shadow-card">
          <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl bg-brand-50 text-brand-700 ring-1 ring-brand-100">
            <Images className="h-6 w-6" aria-hidden="true" />
          </span>
          <h2 className="mt-4 text-base font-semibold text-ink-900">No gallery images yet</h2>
          <p className="mx-auto mt-1 max-w-sm text-sm text-ink-500">
            Upload a photo or paste an image URL and it will appear in the Memories section of the
            homepage.
          </p>
          <button type="button" onClick={openCreate} className="btn-primary mt-5">
            <Plus className="h-4 w-4" aria-hidden="true" />
            Add image
          </button>
        </div>
      )}

      {!loading && images.length > 0 && filteredImages.length === 0 && (
        <div className="rounded-xl border border-ink-200 bg-white p-10 text-center shadow-card">
          <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl bg-brand-50 text-brand-700 ring-1 ring-brand-100">
            <Search className="h-6 w-6" aria-hidden="true" />
          </span>
          <h2 className="mt-4 text-base font-semibold text-ink-900">No matching images</h2>
          <p className="mx-auto mt-1 max-w-sm text-sm text-ink-500">
            No title or category matches &quot;{searchTerm.trim()}&quot;.
          </p>
          <button
            type="button"
            onClick={() => setSearchTerm('')}
            className="btn-outline mt-5"
          >
            Clear search
          </button>
        </div>
      )}

      {!loading && filteredImages.length > 0 && (
        <ul className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {filteredImages.map((image) => {
            const isHidden = image.is_active === false;
            const imageLabel = titleOf(image);

            return (
              <li
                key={image.id}
                className="group flex flex-col overflow-hidden rounded-xl border border-ink-200 bg-white shadow-card transition-[box-shadow,border-color] duration-200 hover:border-brand-200 hover:shadow-card-hover"
              >
                <div className="relative aspect-[4/3] w-full overflow-hidden bg-brand-800">
                  {brokenIds[image.id] ? (
                    <div className="flex h-full w-full flex-col items-center justify-center gap-2 bg-ink-100 text-ink-400">
                      <ImageOff className="h-6 w-6" aria-hidden="true" />
                      <span className="text-2xs font-semibold uppercase tracking-wide">
                        Image unavailable
                      </span>
                    </div>
                  ) : (
                    <img
                      src={resolveImageSrc(image.src || image.image_url)}
                      alt={image.title || 'Gallery image'}
                      loading="lazy"
                      onError={() => markBroken(image.id)}
                      className={`h-full w-full object-cover transition duration-300 group-hover:scale-[1.02] ${
                        isHidden ? 'opacity-50' : ''
                      }`}
                    />
                  )}

                  {isHidden && (
                    <span className="badge absolute left-3 top-3 bg-ink-900 text-white shadow-sm">
                      Hidden
                    </span>
                  )}
                </div>

                <div className="flex flex-1 flex-col gap-2 p-4">
                  <p className="line-clamp-2 text-sm font-semibold leading-snug text-ink-900">
                    {image.title || 'Untitled image'}
                  </p>
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                    <span className="badge-neutral">{image.category || 'Gallery'}</span>
                    <span className="text-2xs font-semibold uppercase tracking-wide text-ink-500">
                      Order {orderOf(image)}
                    </span>
                  </div>

                  <div className="mt-auto flex items-center gap-2 border-t border-ink-100 pt-3">
                    <ActionMenu
                      label={`Actions for ${imageLabel}`}
                      items={[
                        {
                          label: 'Edit image',
                          icon: Pencil,
                          onClick: () => openEdit(image),
                          disabled: busyId === image.id,
                        },
                        {
                          label: isHidden ? 'Show on homepage' : 'Hide from homepage',
                          icon: isHidden ? Eye : EyeOff,
                          onClick: () => toggleActive(image),
                          disabled: busyId === image.id,
                        },
                        {
                          label: 'Delete image',
                          icon: Trash2,
                          variant: 'danger',
                          onClick: () => handleDelete(image),
                          disabled: busyId === image.id,
                        },
                      ]}
                    />

                    {busyId === image.id && (
                      <Loader2
                        className="ml-auto h-4 w-4 animate-spin text-ink-400"
                        aria-hidden="true"
                      />
                    )}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {isFormOpen && (
        <div
          className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-ink-900/60 p-4 sm:py-10"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) closeForm();
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="gallery-form-title"
            className="relative w-full max-w-2xl rounded-xl border border-ink-200 bg-white shadow-card-hover"
          >
            <div className="flex items-start justify-between gap-4 border-b border-ink-100 px-6 py-4">
              <div>
                <h2 id="gallery-form-title" className="text-lg font-semibold text-ink-900">
                  {editing ? 'Edit image' : 'Add image'}
                </h2>
                <p className="mt-0.5 text-sm text-ink-500">
                  {editing
                    ? 'Changes go live in the homepage Memories section immediately.'
                    : 'Upload a photo or point at an image URL.'}
                </p>
              </div>
              <IconButton label="Close dialog" hint="Close" onClick={closeForm} disabled={saving}>
                <X className="h-4 w-4" aria-hidden="true" />
              </IconButton>
            </div>

            <form onSubmit={handleSubmit} noValidate>
              <div className="grid grid-cols-1 gap-4 px-6 py-5 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <label className="label" htmlFor="gallery-title">
                    Title
                  </label>
                  <input
                    id="gallery-title"
                    ref={titleInputRef}
                    type="text"
                    className="input-field"
                    value={form.title}
                    onChange={updateField('title')}
                    placeholder="e.g. 2026 1st Graduation Ceremony"
                    maxLength={140}
                    autoComplete="off"
                  />
                </div>

                <div>
                  <label className="label" htmlFor="gallery-category">
                    Category
                  </label>
                  <input
                    id="gallery-category"
                    type="text"
                    className="input-field"
                    value={form.category}
                    onChange={updateField('category')}
                    placeholder="e.g. Ceremony"
                    list="gallery-category-options"
                    maxLength={60}
                    autoComplete="off"
                  />
                  <datalist id="gallery-category-options">
                    {categories.map((category) => (
                      <option key={category} value={category} />
                    ))}
                  </datalist>
                </div>

                <div>
                  <label className="label" htmlFor="gallery-order">
                    Display order
                  </label>
                  <input
                    id="gallery-order"
                    type="number"
                    min="0"
                    step="1"
                    inputMode="numeric"
                    className="input-field"
                    value={form.displayOrder}
                    onChange={updateField('displayOrder')}
                    placeholder="1"
                  />
                  <p className="mt-1.5 text-2xs text-ink-500">Lower numbers appear first.</p>
                </div>

                <div className="sm:col-span-2">
                  <span className="label" id="gallery-source-label">
                    Image source
                  </span>
                  <div
                    role="group"
                    aria-labelledby="gallery-source-label"
                    className="flex flex-wrap gap-2"
                  >
                    <button
                      type="button"
                      aria-pressed={form.source === 'upload'}
                      onClick={() => setForm((previous) => ({ ...previous, source: 'upload' }))}
                      className={form.source === 'upload' ? 'btn-primary' : 'btn-outline'}
                    >
                      <Upload className="h-4 w-4" aria-hidden="true" />
                      Upload file
                    </button>
                    <button
                      type="button"
                      aria-pressed={form.source === 'url'}
                      onClick={() => setForm((previous) => ({ ...previous, source: 'url' }))}
                      className={form.source === 'url' ? 'btn-primary' : 'btn-outline'}
                    >
                      <Link2 className="h-4 w-4" aria-hidden="true" />
                      Paste URL
                    </button>
                  </div>
                </div>

                {form.source === 'upload' ? (
                  <div className="sm:col-span-2">
                    <label className="label" htmlFor="gallery-file">
                      Image file
                    </label>
                    <input
                      id="gallery-file"
                      type="file"
                      accept="image/jpeg,image/png,image/gif,image/webp"
                      onChange={handleFileChange}
                      className="input-field cursor-pointer text-sm file:mr-3 file:rounded-md file:border-0 file:bg-brand-50 file:px-3 file:py-1.5 file:text-sm file:font-semibold file:text-brand-700"
                    />
                    <p className="mt-1.5 text-2xs text-ink-500">
                      JPEG, PNG, GIF or WebP · up to 10 MB
                      {editing ? ' · leave empty to keep the current image' : ''}
                    </p>
                  </div>
                ) : (
                  <div className="sm:col-span-2">
                    <label className="label" htmlFor="gallery-url">
                      Image URL
                    </label>
                    <input
                      id="gallery-url"
                      type="text"
                      inputMode="url"
                      className="input-field"
                      value={form.imageUrl}
                      onChange={updateField('imageUrl')}
                      placeholder="https://example.com/graduation.jpg or /images/12.jpg"
                      autoComplete="off"
                    />
                    <p className="mt-1.5 text-2xs text-ink-500">
                      Use a full https:// address, or a path already published by the site.
                    </p>
                  </div>
                )}

                <div className="sm:col-span-2">
                  <span className="label">Preview</span>
                  <div className="relative aspect-[16/9] w-full overflow-hidden rounded-lg border border-ink-200 bg-brand-800">
                    {previewSrc && !previewFailed ? (
                      <img
                        src={previewSrc}
                        alt="Selected image preview"
                        className="h-full w-full object-cover"
                        onError={() => setFailedPreview(previewSrc)}
                      />
                    ) : (
                      <div className="flex h-full w-full flex-col items-center justify-center gap-2 bg-ink-100 text-ink-400">
                        {previewFailed ? (
                          <ImageOff className="h-6 w-6" aria-hidden="true" />
                        ) : (
                          <ImageUp className="h-6 w-6" aria-hidden="true" />
                        )}
                        <span className="px-4 text-center text-2xs font-semibold uppercase tracking-wide">
                          {previewFailed
                            ? 'That image could not be loaded'
                            : 'No image selected yet'}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {editing && (
                  <div className="sm:col-span-2">
                    <label htmlFor="gallery-active" className="label flex cursor-pointer items-center gap-2.5">
                      <input
                        id="gallery-active"
                        type="checkbox"
                        checked={form.isActive}
                        onChange={(event) =>
                          setForm((previous) => ({ ...previous, isActive: event.target.checked }))
                        }
                        className="h-4 w-4 rounded border-ink-300 accent-brand-700"
                      />
                      Visible on the homepage
                    </label>
                    <p className="mt-1.5 text-2xs text-ink-500">
                      Untick to keep this photo out of the public gallery without deleting it.
                    </p>
                  </div>
                )}
              </div>

              <div className="flex flex-wrap items-center justify-end gap-3 border-t border-ink-100 px-6 py-4">
                <button
                  type="button"
                  onClick={closeForm}
                  disabled={saving}
                  className="btn-ghost"
                >
                  Cancel
                </button>
                <button type="submit" disabled={saving} className="btn-primary">
                  {saving ? (
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                  ) : (
                    <Check className="h-4 w-4" aria-hidden="true" />
                  )}
                  {saving ? 'Saving…' : editing ? 'Save changes' : 'Add image'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminGallery;
